/**
 * LocalStore Utility.
 *
 * Abstracts common localStorage operations.
 *
 * Unlike SessionStore, localStorage persists across sessions, which makes it
 * suitable as a fallback cache for remote configuration: a previously loaded,
 * known-good config remains available to a brand-new visit on a flaky network.
 *
 */
import BaseStore from './storebase';

/**
 * LocalStorage Utility class.
 */
export default class LocalStore extends BaseStore {

    /**
     * Check if local storage is possible by setting an indicator value.
    */
    public checkAvailability(): void {
        this.set(`${this.prefix}-ct-loc-test`, ['1']);
    }

    /**
     * Passively get the localStorage object located at key.
     * Handles if localStorage is not available by returning an empty array.
     *
     * @param {string} key
     * @return {Array<string>}
     */
    public get(key: string) : Array<string> {
        if (this.status) {
            try {
                let value = window.localStorage.getItem(key);
                if (value) {
                    return JSON.parse(value);
                }
            } catch (error) {
                this.fail(error);
                return [];
            }
        }
        return [];
    }

    /**
     * Passively set the localStorage object located at key with value.
     * Handles if localStorage is not available by returning false.
     *
     * @param {string} key
     * @param {Array<string>} value
     * @return {boolean}
     */
    public set(key: string, value: Array<string>): boolean {
        if (this.status) {
            const jsonValue = JSON.stringify(value);
            try {
                window.localStorage.setItem(key, jsonValue);
                // ensure was set properly
                if (JSON.stringify(this.get(key)) !== jsonValue) {
                    return false;
                }
                return true;
            } catch (error) {
                return this.fail(error);
            }
        }
        return false;
    }

    /**
     * Passively remove the localStorage object located at key.
     *
     * @param {string} key
     */
    public remove(key: string): void {
        if (this.status) {
            try {
                window.localStorage.removeItem(key);
            } catch (error) {
                this.fail(error);
            }
        }
    }

}
